// src/composables/useSmartExit.ts
import { useRouter, type RouteLocationRaw } from 'vue-router'

/**
 * Interface for Vue Router's internal history state.
 * Vue Router adds a 'position' property to the history state.
 */
interface HistoryState {
  position?: number
  [key: string]: unknown
}

export function useFlowHistory(fallbackRoute: RouteLocationRaw) {
  const router = useRouter()

  // 1. SNAPSHOT AT MOUNT
  // We capture the state of history the moment this Fullscreen view opens.
  const historyState = window.history.state as HistoryState
  const entryPosition = historyState?.position ?? 0

  // CRITICAL: We check if there is ANY history before this moment.
  // If 'back' is null, the user landed here directly (Bookmark/New Tab).
  const hasHistoryBeforeMount = historyState?.back !== null

  const exitFlow = () => {
    const currentPosition = (window.history.state as HistoryState)?.position ?? 0

    // SCENARIO 1: DIRECT ENTRY (The Fix)
    // If there was no history when we started, we cannot "rewind" out of the view.
    // We must Force Replace to the fallback to "escape" the fullscreen loop.
    if (!hasHistoryBeforeMount) {
      router.replace(fallbackRoute)
      return
    }

    // SCENARIO 2: NORMAL NAVIGATION
    // We have a valid history behind us. Let's calculate the rewind.
    // We want to go to: (entryPosition - 1)
    const stepsToRewind = entryPosition - 1 - currentPosition

    // Safety: If for some reason the calculation is positive (future),
    // fallback to safety to prevent undefined behavior.
    if (stepsToRewind >= 0) {
      router.replace(fallbackRoute)
      return
    }

    // Execute the clean rewind
    router.go(stepsToRewind)
    if (!hasHistoryBeforeMount) {
      router.replace(fallbackRoute)
      return
    }
  }

  return { exitFlow }
}
